/**
 * Filters job locations for display in hit cards.
 * 
 * Rules:
 * - If there's at least 1 location in Finland, only show:
 *   - Finland locations (e.g., "Helsinki, Finland")
 *   - Remote regional locations (e.g., "Remote - Global", "Remote - Europe")
 * - Don't show non-Finland city locations (e.g., "Berlin, Germany") - users can see these when clicking into the job
 * 
 * @param locations - Array of location strings (with countries, e.g., "Helsinki, Finland")
 * @returns Filtered array of location strings
 */

import { isFinlandLocationStrict } from '../geocoding/is-finland-location';

/**
 * Checks if a location is a remote regional location like "Remote - Global" or "Remote - Europe"
 */
function isRemoteRegionalLocation(location: string | undefined | null): boolean {
  if (!location || typeof location !== 'string') return false;
  
  const normalized = location.toLowerCase().trim();
  
  // Remote prefixes in different languages
  const remotePrefixes = [
    'remote -',
    'etätyö -',
    'etä -',
    'distans -',
    'fjärr -'
  ];
  
  // Check if it starts with a remote prefix
  const hasRemotePrefix = remotePrefixes.some(prefix => normalized.startsWith(prefix));
  
  if (hasRemotePrefix) {
    // Extract the part after "Remote -" (or equivalent)
    const afterRemote = normalized.replace(/^(remote|etätyö|etä|distans|fjärr)\s*-\s*/i, '').trim();
    
    // Regional options that could include Finland
    const regionalOptions = [
      'global',
      'globaali',
      'globalt',
      'worldwide',
      'world',
      'europe',
      'eurooppa',
      'europa',
      'eu',
      'emea',
      'mena',
      'nordics',
      'nordic',
      'pohjoismaat',
      'scandinavia',
      'skandinavien',
      'baltics',
      'baltic',
      'benelux',
      'north america',
      'asia',
      'africa',
      'oceania',
      'south america',
      'anywhere'
    ];
    
    // Check if it matches a regional option
    return regionalOptions.some(region => afterRemote === region || afterRemote.startsWith(region + ' '));
  }
  
  // Standalone "Remote" (without prefix format)
  if (normalized === 'remote' || 
      normalized === 'etätyö' || 
      normalized === 'distans' || 
      normalized === 'fjärr') {
    return true;
  }
  
  // "Anywhere" variations
  if (normalized.includes('anywhere')) {
    return true;
  }
  
  return false;
}

/**
 * Filters locations based on the business rules:
 * - If there's at least 1 Finland location, only show Finland locations and remote regional locations
 * - Otherwise, show all locations
 */
export function filterJobLocations(locations: (string | undefined | null)[]): string[] {
  if (!locations || locations.length === 0) return [];
  
  // Filter out null/undefined and convert to strings
  const validLocations = locations
    .filter((loc): loc is string => typeof loc === 'string' && loc.trim() !== '')
    .map(loc => loc.trim());
  
  if (validLocations.length === 0) return [];
  
  // Check if there's at least one Finland location
  const hasFinlandLocation = validLocations.some(loc => isFinlandLocationStrict(loc));
  
  if (hasFinlandLocation) {
    // Only show Finland locations and remote regional locations
    return validLocations.filter(loc => 
      isFinlandLocationStrict(loc) || isRemoteRegionalLocation(loc)
    );
  }
  
  // If no Finland locations, show all locations
  return validLocations;
}
